import {DestroyRef, inject, Injectable} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MatSnackBar, MatSnackBarRef, TextOnlySnackBar} from '@angular/material/snack-bar';
import {Subject, debounceTime} from 'rxjs';
import {ApiArea} from '../utils/api-url.util';
import {ErrorMessageService} from './error-message.service';


/**
 * Zeitfenster, in dem mehrere Fehlermeldungen zu einer Anzeige zusammenfallen.
 *
 * MatSnackBar hat keine Queue - jedes open() ersetzt die offene Snackbar
 * sofort. Bei einer Fehlerserie (Filtertippen, Paginieren, schnelles
 * Navigieren) würde das die Anzeige flackern lassen.
 */
const DISPLAY_DEBOUNCE_MS = 100;

/**
 * Obergrenze für die Endpunkt-Map. Der Endpunkt-Schlüssel ist bereits auf das
 * Ressourcen-Segment reduziert, sodass die Menge strukturell begrenzt ist -
 * diese Grenze ist eine zusätzliche Absicherung.
 */
const MAX_TRACKED_ENDPOINTS = 50;

type StateKind = 'connectivity' | 'endpoint';

interface TrackedError {
  readonly status: number;
  readonly area: ApiArea;
  generation: number;
  acknowledged: boolean;
  sequence: number;
}

interface SelectedState {
  readonly kind: StateKind;
  readonly key: string;
  readonly error: TrackedError;
}

interface DisplayedState {
  readonly kind: StateKind;
  readonly key: string;
  readonly generation: number;
}

/**
 * Kontext einer geöffneten Snackbar. Bewusst pro MatSnackBarRef gehalten und
 * über die Closure an das afterDismissed-Callback gebunden: `afterDismissed`
 * ist asynchron, ein gemeinsames Instanzfeld könnte bei überlappenden
 * Schließvorgängen dem falschen Ref zugeordnet werden.
 */
interface OpenContext {
  readonly kind: StateKind;
  readonly key: string;
  readonly generation: number;
}


/**
 * Hält fest, welche Abrufe des Export-Service fehlgeschlagen sind, und zeigt
 * dazu eine dauerhafte Snackbar.
 *
 * Muss `providedIn: 'root'` sein, weil der ServiceErrorInterceptor im
 * Root-Injector lebt und beide dieselbe Instanz brauchen.
 *
 * Zwei Zustandsklassen:
 * - Konnektivität (Status 0) pro Bereich. Der Bereich ist Teil der Identität,
 *   weil Admin und Wizard unterschiedliche Texte brauchen und der Service
 *   einen Bereichswechsel überlebt.
 * - Endpunktfehler (alle übrigen Status) pro Endpunkt-Schlüssel.
 */
@Injectable({
  providedIn: 'root',
})
export class ServiceAvailabilityService {

  private readonly _destroyRef = inject(DestroyRef);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _errorMessageService = inject(ErrorMessageService);

  private readonly _connectivity = new Map<ApiArea, TrackedError>();
  private readonly _endpointErrors = new Map<string, TrackedError>();
  private readonly _openContexts = new Map<MatSnackBarRef<TextOnlySnackBar>, OpenContext>();

  private readonly _refresh$ = new Subject<void>();

  private _ref?: MatSnackBarRef<TextOnlySnackBar>;
  private _displayed?: DisplayedState;
  private _sequence = 0;

  /**
   * Monoton steigender Zähler für Generationen.
   *
   * Bewusst serviceweit und unabhängig von den Maps: Würde die Generation aus
   * dem vorhandenen Eintrag abgeleitet, begänne sie nach einem `reportSuccess`
   * wieder bei 1. Ein verzögertes afterDismissed-Callback könnte dann einen
   * neu aufgetretenen Fehler quittieren, obwohl es zu einem früheren
   * Lebenszyklus desselben Schlüssels gehört.
   */
  private _generationSequence = 0;

  constructor() {
    this._refresh$
      .pipe(
        debounceTime(DISPLAY_DEBOUNCE_MS),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe(() => this.updateDisplay());
  }

  /**
   * Meldet einen fehlgeschlagenen Request.
   *
   * Der Zustand wird sofort synchron gesetzt; nur die Anzeige ist entprellt.
   * Andernfalls gingen bei einer Fehlerserie Zustände verloren, weil
   * `debounceTime` ausschließlich den letzten Wert weitergibt.
   *
   * @param area Bereich, aus dem der Request stammt
   * @param status HTTP-Status; 0 bedeutet, dass keine Antwort empfangen wurde
   * @param endpointKey Auf das Ressourcen-Segment reduzierter Pfad
   */
  reportFailure(
    area: ApiArea,
    status: number,
    endpointKey: string,
  ): void {
    const isConnectivity = status === 0;
    const map: Map<string, TrackedError> = isConnectivity
      ? this._connectivity as Map<string, TrackedError>
      : this._endpointErrors;
    const key = isConnectivity ? area : endpointKey;

    const existing = map.get(key);
    if (existing && existing.status === status) {
      // INFO: Derselbe Fehler erneut - Reihenfolge aktualisieren, eine
      // bestehende Quittierung bleibt erhalten.
      existing.sequence = ++this._sequence;
    } else {
      // INFO: Neuer Zustand oder Statuswechsel am selben Endpunkt. Ein Wechsel
      // von 503 auf 403 ist ein anderer Fehler mit anderem Text und darf nicht
      // als quittiert gelten.
      map.set(key, {
        status,
        area,
        generation: ++this._generationSequence,
        acknowledged: false,
        sequence: ++this._sequence,
      });
    }

    if (!isConnectivity) {
      this.enforceEndpointLimit();
    }

    this._refresh$.next();
  }

  /**
   * Meldet einen erfolgreichen Request.
   *
   * Löscht den Konnektivitätszustand aller Bereiche - kommt irgendein Request
   * durch, besteht die Verbindung wieder - und aus den Endpunktfehlern nur den
   * Eintrag desselben Schlüssels.
   *
   * @param endpointKey Auf das Ressourcen-Segment reduzierter Pfad
   */
  reportSuccess(
    endpointKey: string,
  ): void {
    const hadConnectivityError = this._connectivity.size > 0;
    const hadEndpointError = this._endpointErrors.delete(endpointKey);

    this._connectivity.clear();

    if (hadConnectivityError || hadEndpointError) {
      this._refresh$.next();
    }
  }

  /**
   * Wählt den anzuzeigenden Zustand und gleicht die Snackbar daran an.
   */
  private updateDisplay(): void {
    const next = this.selectState();

    if (!next) {
      this._ref?.dismiss();
      return;
    }

    if (
      this._displayed
      && this._displayed.kind === next.kind
      && this._displayed.key === next.key
      && this._displayed.generation === next.error.generation
    ) {
      return;
    }

    // INFO: Direkt öffnen, auch wenn noch eine eigene Snackbar offen ist -
    // Material schließt die bisherige selbst. Ein zweistufiges Vorgehen
    // (erst schließen, im afterDismissed-Callback öffnen) hätte eine Lücke:
    // Material emittiert afterDismissed erst nach der Exit-Animation, und
    // eine in diesem Fenster von fremder Stelle geöffnete Snackbar würde
    // durch das nachgelagerte Öffnen wieder verdrängt.
    this.open(next);
  }

  /**
   * Konnektivität hat Vorrang, weil sie die übergeordnete Ursache ist. Bei
   * mehreren Kandidaten gewinnt der zuletzt gemeldete - praktisch der Bereich,
   * in dem der Nutzer sich gerade bewegt.
   */
  private selectState(): SelectedState | undefined {
    const candidates: SelectedState[] = [];

    for (const [area, error] of this._connectivity) {
      if (!error.acknowledged) {
        candidates.push({kind: 'connectivity', key: area, error});
      }
    }

    if (candidates.length === 0) {
      for (const [key, error] of this._endpointErrors) {
        if (!error.acknowledged) {
          candidates.push({kind: 'endpoint', key, error});
        }
      }
    }

    return candidates.sort((a, b) => b.error.sequence - a.error.sequence)[0];
  }

  private open(
    next: SelectedState,
  ): void {
    const message = next.error.area === 'admin'
      ? this._errorMessageService.handleAdminDataLoadHttpStatus(next.error.status)
      : this._errorMessageService.handleCustomerConnectionHttpStatus(next.error.status);

    const ref = this._snackBar.open(
      message,
      'Schließen',
      {
        // INFO: duration 0 bedeutet kein Auto-Dismiss - die Meldung bleibt,
        // bis der Nutzer sie schließt oder der Fehler behoben ist.
        duration: 0,
        panelClass: 'error',
      },
    );

    const context: OpenContext = {
      kind: next.kind,
      key: next.key,
      generation: next.error.generation,
    };

    this._ref = ref;
    this._displayed = context;
    this._openContexts.set(ref, context);

    ref.afterDismissed()
      .pipe(
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe(info => this.handleDismissed(ref, context, info.dismissedByAction));
  }

  private handleDismissed(
    ref: MatSnackBarRef<TextOnlySnackBar>,
    context: OpenContext,
    dismissedByAction: boolean,
  ): void {
    this._openContexts.delete(ref);

    // INFO: Nur aufräumen, wenn dieser Ref noch der aktuelle ist. Wurde er
    // durch einen eigenen Wechsel ersetzt, gehört _ref bereits zur neuen
    // Snackbar und darf nicht zurückgesetzt werden.
    if (this._ref === ref) {
      this._ref = undefined;
      this._displayed = undefined;
    }

    if (!dismissedByAction) {
      // INFO: Entweder haben wir selbst ersetzt beziehungsweise geschlossen -
      // dann ist der Nachfolger bereits offen oder es gibt keinen - oder eine
      // fremde Snackbar hat verdrängt. In beiden Fällen darf hier nichts
      // geöffnet werden, sonst würde eine fremde Meldung unterdrückt. Neue
      // Fehler und Erfolge lösen ohnehin eine erneute Auswertung aus, sodass
      // ein verdrängter Zustand nicht verloren geht.
      return;
    }

    // INFO: Der Nutzer hat quittiert und erwartet, dass ein weiterer offener
    // Fehler sichtbar wird.
    this.acknowledge(context);
    this.updateDisplay();
  }

  /**
   * Quittiert genau den Zustand, der angezeigt wurde. Die Generation wird
   * mitgeprüft, damit ein zwischenzeitlich neu aufgetretener Fehler nicht
   * versehentlich als quittiert gilt.
   */
  private acknowledge(
    context: OpenContext,
  ): void {
    const map: Map<string, TrackedError> = context.kind === 'connectivity'
      ? this._connectivity as Map<string, TrackedError>
      : this._endpointErrors;

    const error = map.get(context.key);
    if (error && error.generation === context.generation) {
      error.acknowledged = true;
    }
  }

  private enforceEndpointLimit(): void {
    if (this._endpointErrors.size <= MAX_TRACKED_ENDPOINTS) {
      return;
    }

    const removable = [...this._endpointErrors.entries()]
      .filter(([, error]) => error.acknowledged)
      .sort((a, b) => a[1].sequence - b[1].sequence);

    const candidates = removable.length > 0
      ? removable
      : [...this._endpointErrors.entries()].sort((a, b) => a[1].sequence - b[1].sequence);

    const removeCount = this._endpointErrors.size - MAX_TRACKED_ENDPOINTS;
    for (const [key] of candidates.slice(0, removeCount)) {
      this._endpointErrors.delete(key);
    }
  }
}
